import { useState } from 'react';
import {
  useHistory,
  matchPath,
  useRouteMatch,
  useLocation,
  generatePath,
} from 'react-router-dom';
import queryString from 'query-string';
import { t } from 'ttag';

import {
  collections,
  dayDiscount,
  looksCollection,
  newArrivals,
  publicMemberProfile,
  swapCollection,
} from '@urls';
import { FILTER_QUERIES } from '@constants';
import { useCatalogRouteMatch, useIsClient } from '@utils/common';
import { SelectOption } from '@ui/Select';
import { HeaderSearch } from '@components/HeaderSearch';

import { useHeaderSearchQuery } from './gql/graphql.v3.generated';

export interface RootCatalog {
  id: number,
  name: string,
  slug: string,
}

interface Props {
  className?: string,
  rootCatalogs?: RootCatalog[],
  isMobile?: boolean,
}

export const HeaderSearchContainer: React.FC<Props> = ({
  className,
  rootCatalogs,
  isMobile,
}) => {
  const history = useHistory();
  const location = useLocation();
  const catalogMatch = useCatalogRouteMatch();
  const collectionMatch = useRouteMatch(collections);
  const looksCollectionMatch = useRouteMatch(looksCollection);
  const publicMemberProfileMatch = useRouteMatch<{ username: string }>(publicMemberProfile);
  const isClient = useIsClient();

  const defaultSearchOption = {
    value: '/clothes',
    label: t`Всі категорії`,
  };

  const customSearchOptions = [
    {
      value: newArrivals,
      label: t`Новинки`,
    },
    {
      value: dayDiscount,
      label: t`Знижки`,
    },
    {
      value: swapCollection,
      label: t`SWAP-паті`,
    },
  ]
    .concat(
      collectionMatch || looksCollectionMatch ? [
        {
          value: location.pathname,
          label: t`По колекції`,
        },
      ] : [],
    )
    .concat(
      publicMemberProfileMatch ? [
        {
          value: generatePath(publicMemberProfile, {
            username: publicMemberProfileMatch.params.username,
          }),
          label: t`У продавця`,
        },
      ] : [],
    );

  const customSearchOption = customSearchOptions.find(
    (option) => history.location.pathname !== '/' && matchPath(option.value, history.location.pathname),
  );

  const [selectedCatalog, setSelectedCatalog] = useState<SelectOption<string>>(
    () => customSearchOption || defaultSearchOption,
  );

  const namespace = catalogMatch && catalogMatch.params.namespace;
  const catalogSlug = catalogMatch && ('slug' in catalogMatch.params)
    ? catalogMatch.params.slug
    : '';

  const { data } = useHeaderSearchQuery({
    variables: {
      catalogSlug,
    },
    context: {
      batch: true,
    },
    skip: !isClient,
    onCompleted(response) {
      if (namespace) {
        if (response.catalog?.slug) {
          setSelectedCatalog({
            value: `${namespace}/${response.catalog.slug}`,
            label: response.catalog.name,
          });
        } else {
          const rootCatalog = rootCatalogs?.find(
            (catalog) => catalog.slug === namespace,
          );
          if (rootCatalog) {
            setSelectedCatalog({
              value: rootCatalog.slug,
              label: rootCatalog.name,
            });
          }
        }
      } else {
        setSelectedCatalog(customSearchOption || defaultSearchOption);
      }
    },
  });

  const handleSelectCatalog = (catalog: SelectOption<string>) => {
    setSelectedCatalog(catalog);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    const isSPAFeed = customSearchOptions.some(
      (option) => option.value === selectedCatalog.value,
    );

    if (isSPAFeed) {
      e.preventDefault();
      // @ts-ignore
      const searchText = e.currentTarget.elements[FILTER_QUERIES.SEARCH_TEXT].value;

      history.push({
        pathname: selectedCatalog.value,
        search: searchText
          ? queryString.stringify({
            search_text: searchText,
          }) : undefined,
      });
    }
  };

  let catalogsOptions: SelectOption<string>[] = [];

  if (data) {
    const rootCatalogOptions = rootCatalogs?.map((catalog) => ({
      value: catalog.slug,
      label: catalog.name,
    })) || [];

    if (data.catalog) {
      catalogsOptions = [
        {
          value: data.catalog.slug || '',
          label: data.catalog.name,
        },
      ].concat([
        defaultSearchOption,
        ...rootCatalogOptions,
      ]);
    } else {
      catalogsOptions = [defaultSearchOption]
        .concat(customSearchOption ? [customSearchOption] : [])
        .concat(rootCatalogOptions);
    }
  }

  return (
    <HeaderSearch
      selectedCatalog={selectedCatalog}
      catalogsOptions={catalogsOptions}
      className={className}
      isMobile={isMobile}
      onSelectCatalog={handleSelectCatalog}
      onSubmit={handleSearch}
    />
  );
};
